var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Signer } from "ethers";
import { getSchema } from "./shared/ajv";
import * as utils from "./shared/utils";
import { validateSchema as validate } from "./shared/validate";
import { SchemaId } from "./shared/@types/document";
import { SigningKey } from "./shared/@types/sign";
import * as v2 from "./2.0/types";
import { wrapDocument as wrapDocumentV2, wrapDocuments as wrapDocumentsV2 } from "./2.0/wrap";
import { signDocument as signDocumentV2 } from "./2.0/sign";
import { verify } from "./2.0/verify";
import { obfuscateDocument as obfuscateDocumentV2 } from "./2.0/obfuscate";
import * as v3 from "./3.0/types";
import { wrapDocument as wrapDocumentV3, wrapDocuments as wrapDocumentsV3 } from "./3.0/wrap";
import { signDocument as signDocumentV3 } from "./3.0/sign";
import { verify as verifyV3 } from "./3.0/verify";
import { digestCredential as digestCredentialV3 } from "./3.0/digest";
import { obfuscateVerifiableCredential as obfuscateVerifiableCredentialV3 } from "./3.0/obfuscate";
import { verify as verifyV4 } from "./4.0/verify";
import { obfuscateVerifiableCredential as obfuscateVerifiableCredentialV4, } from "./4.0/obfuscate";
import { v4Diagnose } from "./4.0/diagnose";
import { isV4WrappedDocument } from "./4.0/types";
export function wrapDocument(data, options) {
    return wrapDocumentV2(data, { externalSchemaId: options === null || options === void 0 ? void 0 : options.externalSchemaId });
}
export function wrapDocuments(dataArray, options) {
    return wrapDocumentsV2(dataArray, { externalSchemaId: options === null || options === void 0 ? void 0 : options.externalSchemaId });
}
/**
 * @deprecated will be removed in the next major release in favour of OpenAttestation v4.0 (more info: https://github.com/Open-Attestation/open-attestation/tree/alpha)
 */
export function __unsafe__use__it__at__your__own__risks__wrapDocument(data, options) {
    return wrapDocumentV3(data, options !== null && options !== void 0 ? options : { version: SchemaId.v3 });
}
/**
 * @deprecated will be removed in the next major release in favour of OpenAttestation v4.0 (more info: https://github.com/Open-Attestation/open-attestation/tree/alpha)
 */
export function __unsafe__use__it__at__your__own__risks__wrapDocuments(dataArray, options) {
    return wrapDocumentsV3(dataArray, options !== null && options !== void 0 ? options : { version: SchemaId.v3 });
}
export const validateSchema = (document) => {
    if (utils.isWrappedV2Document(document) || (document === null || document === void 0 ? void 0 : document.version) === SchemaId.v2)
        return validate(document, getSchema(SchemaId.v2)).length === 0;
    else if (utils.isWrappedV3Document(document) || (document === null || document === void 0 ? void 0 : document.version) === SchemaId.v3)
        return validate(document, getSchema(SchemaId.v3)).length === 0;
    else if (isV4WrappedDocument(document)) {
        return v4Diagnose({ document, kind: "wrapped", debug: false, mode: "strict" }).length === 0;
    }
    return validate(document, getSchema(`${(document === null || document === void 0 ? void 0 : document.version) || SchemaId.v2}`)).length === 0;
};
export function verifySignature(document) {
    if (utils.isWrappedV2Document(document))
        return verify(document);
    else if (utils.isWrappedV3Document(document))
        return verifyV3(document);
    else if (isV4WrappedDocument(document))
        return verifyV4(document);
    throw new Error("Unsupported document type: Only OpenAttestation v2, v3 or v4 documents can be signature verified");
}
export function digest(document, salts, obfuscatedData) {
    if (utils.isRawV3Document(document))
        return digestCredentialV3(document, salts, obfuscatedData);
    throw new Error("Unsupported credential type: This function only supports digest generation for OpenAttestation v3 credentials");
}
export function obfuscate(document, fields) {
    if (utils.isWrappedV2Document(document))
        return obfuscateDocumentV2(document, fields);
    else if (utils.isWrappedV3Document(document))
        return obfuscateVerifiableCredentialV3(document, fields);
    else if (isV4WrappedDocument(document))
        return obfuscateVerifiableCredentialV4(document, fields);
    throw new Error("Unsupported document type: Only OpenAttestation v2, v3 or v4 documents can be obfuscated");
}
export function signDocument(document, algorithm, keyOrSigner) {
    return __awaiter(this, void 0, void 0, function* () {
        // rj was worried it could happen deep in the code, so I moved it to the boundaries
        if (!SigningKey.guard(keyOrSigner) && !Signer.isSigner(keyOrSigner)) {
            throw new Error(`Either a keypair or ethers.js Signer must be provided`);
        }
        let results;
        if (utils.isWrappedV2Document(document))
            results = signDocumentV2(document, algorithm, keyOrSigner);
        else if (utils.isWrappedV3Document(document))
            results = signDocumentV3(document, algorithm, keyOrSigner);
        if (results)
            return results;
        throw new Error("Unsupported document type: Only OpenAttestation v2 or v3 documents can be signed");
    });
}
export { digestDocument } from "./2.0/digest";
export { checkProof, MerkleTree } from "./shared/merkle";
export { digest as digestCredential };
export { obfuscate as obfuscateDocument };
export { utils };
export * from "./shared/@types/document";
export * from "./shared/@types/sign";
export * from "./shared/signer";
export { getData } from "./shared/utils"; // keep it to avoid breaking change, moved from privacy to utils
export { v2 };
export { v3 };
import * as v4_1 from "./4.0/exports";
export { v4_1 as v4 };
